import IconeSubstituicao from '@mui/icons-material/SwapVerticalCircle';
import Warning from '@mui/icons-material/Warning';
import { CircularProgress, Divider, Link, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { green, grey, lightBlue, red, yellow } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

import '../css/processo.css';
import { SITUACAO_IMPEDIMENTOS_PROCESSO } from '@/dominio/SituacaoImpedimentosProcesso';

import QuorumBusiness from '../dominio/QuorumBusiness';
import Configuracao from '../util/Configuracao';
import DescricaoClasseProcesso from './DescricaoClasseProcesso';
import IconeConsultaPublica from './IconeConsultaPublica';
import JulgamentosAnteriores from './JulgamentosAnteriores';
import LabelNumeroProcesso from './LabelNumeroProcesso';
import PartesProcesso from './PartesProcesso';
import PedidosSustentacaoOral from './PedidosSustentacaoOral';
import ReactComponent from './ReactComponent';
import ComplementoAnotacao from './sessao/ComplementoAnotacao';
import Indicadores from './sessao/Indicadores';

const ESPACAMENTO_ENTRE_INFORMACOES = '5px';
const COR_TITULO_INFORMACOES_PROCESSO = lightBlue[900];

class InformacoesProcesso extends ReactComponent {
    abrirDialog = false;

    tooltipSubstituicao = (magistrado) => {
        if (magistrado.magistradoSubstituto && magistrado.magistradoSubstituto.sigla.length > 0) {
            return `Substituído por ${magistrado.magistradoSubstituto.nome} (${magistrado.magistradoSubstituto.sigla})`;
        }

        if (magistrado.magistradoSubstituido) {
            return `Substituindo ${magistrado.magistradoSubstituido.nome} (${magistrado.magistradoSubstituido.sigla})`;
        }
    };

    handleCloseDialog = () => {
        this.abrirDialog = false;
    };

    tooltipMovimento = (movimento) => {
        if (!movimento) {
            return;
        }

        let descricaoMovimento = movimento.descricao
            ? this.corrigirString(movimento.descricao)
            : `<span style="color: orange">"O movimento ${movimento.numeroMovimentoCnj} não está disponível neste sistema."</span>`;

        if (movimento.complementos && movimento.complementos.length > 0) {
            descricaoMovimento = this.dominioStore.substituirDescricoesGruposComplemento(descricaoMovimento, movimento);
            descricaoMovimento = this.dominioStore.substituirDescricoesComplementos(
                descricaoMovimento,
                movimento,
                movimento.complementos
            );
        }
        return (
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: movimento.numeroMovimentoCnj + ' - ' + descricaoMovimento }}
                style={{ fontSize: '14px' }}
            />
        );
    };

    extrairSigla = (obj) => {
        if (!obj) return '';
        if (typeof obj === 'string') return obj.trim().toUpperCase();
        if (typeof obj?.sigla === 'string') return obj.sigla.trim().toUpperCase();
        if (typeof obj?.magistrado?.sigla === 'string') return obj.magistrado.sigla.trim().toUpperCase();
        return '';
    };

    extrairSituacao = (situacao) => {
        if (!situacao) return '';
        if (typeof situacao === 'string') return situacao;
        return situacao.codigo || situacao.value || situacao.descricao || '';
    };

    extrairSiglaImpedido = (impedimento) => {
        if (!impedimento) return '';
        const mag = impedimento.magistrado || impedimento;

        if (mag?.magistradoSubstituido) {
            return this.extrairSigla(mag.magistradoSubstituido);
        }
        return this.extrairSigla(mag);
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            abrirDialog: observable,
            handleCloseDialog: action,
            registro: computed,
            processo: computed,
            composicao: computed,
            magistradosPresentes: computed,
            presidenteSessao: computed,
            magistradosAusentes: computed,
            impedimentos: computed,
            magistradosImpedidosSuspeitos: computed,
            mapaMagistradosImpedidosSuspeitos: computed,
            linkConsultaProcesso: computed,
            isComposicaoPadraoDiferente: computed,
            composicaoHabilitada: computed,
            tooltipSubstituicao: false,
            tooltipMovimento: false,
            extrairSigla: false,
            extrairSituacao: false,
            extrairSiglaImpedido: false,
            corChipIndicador: false,
            isImpedimentoComposicao: false,
            tooltipMagistrado: false,
            renderMagistrados: false,
            renderSubstituicao: false,
            getNomeMagistrado: false,
            existePresidenteComposicao: false,
            isImpedido: false,
            isObedeceQuorumComposicao: false,
            alertasComposicaoTooltipTitle: false,
            renderAlertasComposicao: false,
            ocultarRepresentantes: false,
            render: false,
            renderInformacoesGeraisProcesso: false,
            renderInformacoesGeraisProcessoVisualizacaoPregaoProcesso: false,
            mensagensAlertaDetalhes: false,
            renderDetalhes: false,
            renderDetalhesPregaoProcesso: false,
            tituloVistor: false,
            renderAlertasImpedimento: false,
            renderAssuntosProcessuais: false,
            renderUltimoAndamento: false,
            renderUltimaConclusaoAutosEm: false,
            renderUltimaDitribuicaoEm: false,
            renderUltimaLiberacaoPautaEm: false,
            renderLiberacaoPauta: false,
            renderComposicao: false
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get processo() {
        return this.registro.processo;
    }

    get composicao() {
        if (!this.registro) {
            return [];
        }

        const siglaRelator = this.registro.processo.relator?.sigla;
        const siglaRedator = this.registro.processo.redator?.sigla;

        return this.registro.composicao.toSorted((comp1, comp2) => {
            if (comp1.magistrado.sigla === siglaRelator) return -1;
            if (comp2.magistrado.sigla === siglaRelator) return 1;
            if (comp1.magistrado.sigla === siglaRedator) return -1;
            if (comp2.magistrado.sigla === siglaRedator) return 1;
            return 0;
        });
    }

    corChipIndicador(corEtiqueta) {
        switch (corEtiqueta) {
            case 'Laranja':
            case 'LARANJA': {
                return 'darkorange';
            }
            case 'Amarelo':
            case 'AMARELO': {
                return yellow[600];
            }
            case 'Vermelho':
            case 'VERMELHO': {
                return red[400];
            }
            default: {
                return grey[400];
            }
        }
    }

    get magistradosPresentes() {
        const comp = this.composicao.filter((composicao) => composicao.magistrado.sigla);

        const presentes = comp
            .filter((composicao) => {
                const { magistrado } = composicao;

                if (magistrado.ausente || magistrado.impedido || magistrado.suspeito) {
                    return false;
                }

                const possuiSubstituto = Boolean(magistrado.magistradoSubstituto?.sigla);
                return !possuiSubstituto;
            })
            .map((composicao) => {
                const magistradoExibicao = { ...composicao.magistrado };

                if (this.mapaMagistradosImpedidosSuspeitos.has(magistradoExibicao.sigla)) {
                    const magistradoNoMapaImpedidosSuspeitos = this.mapaMagistradosImpedidosSuspeitos.get(
                        magistradoExibicao.sigla
                    );

                    magistradoExibicao.cor = magistradoNoMapaImpedidosSuspeitos.cor;
                    magistradoExibicao.fontWeight = magistradoNoMapaImpedidosSuspeitos.fontWeight;
                    magistradoExibicao.origensImpedimento = magistradoNoMapaImpedidosSuspeitos.origensImpedimento;
                }

                return magistradoExibicao;
            });

        return presentes;
    }

    get presidenteSessao() {
        return this.composicao.find((composicao) => {
            return composicao.magistrado.presidenteSessao;
        });
    }

    get magistradosAusentes() {
        if (!this.composicao) {
            return [];
        }

        const ausentesMap = new Map();

        for (const comp of this.composicao) {
            const substituido = comp.magistrado?.magistradoSubstituido;
            const siglaSubstituido = this.extrairSigla(substituido);

            if (siglaSubstituido) {
                const temImpedimento = (this.impedimentos || []).some((imped) => {
                    const siglaImped = this.extrairSiglaImpedido(imped);
                    const situacao = this.extrairSituacao(imped.situacaoImpedimento);
                    return siglaImped === siglaSubstituido && Boolean(situacao);
                });

                if (!temImpedimento) {
                    const dadosSubstituido =
                        typeof substituido === 'object' ? toJS(substituido) : { sigla: siglaSubstituido };
                    ausentesMap.set(siglaSubstituido, {
                        ...dadosSubstituido,
                        magistradoSubstituto: {
                            nome: comp.magistrado.nome,
                            sigla: comp.magistrado.sigla,
                            cpf: comp.magistrado.cpf,
                            codigoCargo: comp.magistrado.codigoCargo,
                            genero: comp.magistrado.genero
                        }
                    });
                }
            }
        }

        for (const comp of this.composicao) {
            const mag = comp.magistrado;
            const sigla = this.extrairSigla(mag);

            if (sigla && mag?.ausente && !ausentesMap.has(sigla)) {
                ausentesMap.set(sigla, { ...toJS(mag) });
            }
        }

        return ausentesMap.values().toArray();
    }

    get impedimentos() {
        return this.registro.impedimentos || [];
    }

    isImpedimentoComposicao(impedimento) {
        return this.sessaoStore.isImpedimentoComposicao(this.registro, impedimento);
    }

    get magistradosImpedidosSuspeitos() {
        const listaImpedimentos = [...(this.impedimentos || [])]
            .filter((impedimento) => {
                const sigla = this.extrairSiglaImpedido(impedimento);
                const situacao = this.extrairSituacao(impedimento.situacaoImpedimento);
                return Boolean(sigla) && Boolean(situacao);
            })
            .map((impedimento) => {
                const rawMagistrado = impedimento.magistrado || impedimento;
                const rawMagJS = typeof rawMagistrado === 'object' ? toJS(rawMagistrado) : { sigla: rawMagistrado };

                let magistrado;
                let substitutoDireto = null;

                if (rawMagJS.magistradoSubstituido) {
                    magistrado = toJS(rawMagJS.magistradoSubstituido);
                    const { nome, sigla, cpf, codigoCargo, genero } = rawMagJS;
                    substitutoDireto = { nome, sigla, cpf, codigoCargo, genero };
                } else {
                    magistrado = rawMagJS;
                }

                const siglaMagistrado = this.extrairSigla(magistrado);
                const situacao = this.extrairSituacao(impedimento.situacaoImpedimento);

                const magistradoNaComposicao = (this.composicao || []).some(
                    (comp) => this.extrairSigla(comp.magistrado) === siglaMagistrado
                );

                magistrado.origemImpedimento =
                    situacao === 'IMPEDIMENTO_DECLARADO' ? 'Impedimento declarado' : 'Possível impedimento';

                let magistradoSubstituto = substitutoDireto;

                if (!magistradoSubstituto) {
                    const substituto = (this.composicao || []).find((comp) => {
                        const siglaSub = this.extrairSigla(comp.magistrado?.magistradoSubstituido);
                        return siglaSub === siglaMagistrado;
                    });

                    if (substituto) {
                        const { nome, sigla, cpf, codigoCargo, genero } = substituto.magistrado;
                        magistradoSubstituto = { nome, sigla, cpf, codigoCargo, genero };
                    }
                }

                if (magistradoSubstituto) {
                    magistrado.magistradoSubstituto = magistradoSubstituto;
                }

                if (magistradoNaComposicao) {
                    magistrado.fontWeight = 'bold';
                    magistrado.origemImpedimento += ' cadastrado na composição do processo';

                    magistrado.cor =
                        situacao === 'IMPEDIMENTO_DECLARADO'
                            ? SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_NA_COMPOSICAO.cor
                            : SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_NA_COMPOSICAO.cor;
                } else {
                    magistrado.origemImpedimento += ' fora da composição do processo';

                    magistrado.cor =
                        situacao === 'IMPEDIMENTO_DECLARADO'
                            ? SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_FORA_DA_COMPOSICAO.cor
                            : SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_FORA_DA_COMPOSICAO.cor;
                }

                if (magistradoSubstituto) {
                    magistrado.fontWeight = 'normal';
                    magistrado.cor = grey[600];
                    magistrado.origemImpedimento += ` — substituído por ${magistradoSubstituto.nome} (${magistradoSubstituto.sigla})`;
                }

                return magistrado;
            });

        const listaSuspeições = (this.composicao || [])
            .filter((composicao) => this.extrairSigla(composicao.magistrado) && composicao.magistrado?.suspeito)
            .map((composicao) => {
                const magistrado = toJS(composicao.magistrado);
                magistrado.origemImpedimento = 'Suspeição cadastrada na composição do processo';
                return magistrado;
            });

        const todosMagistrados = [...listaImpedimentos, ...listaSuspeições];

        const mapaMagistrados = new Map();

        for (const magistrado of todosMagistrados) {
            const sigla = this.extrairSigla(magistrado);

            if (!sigla) continue;

            if (!mapaMagistrados.has(sigla)) {
                mapaMagistrados.set(sigla, toJS(magistrado));
            }

            const magistradoAcumulado = mapaMagistrados.get(sigla);
            magistradoAcumulado.cor ||= magistrado.cor;

            if (magistrado.origemImpedimento) {
                magistradoAcumulado.origensImpedimento ||= [];
                magistradoAcumulado.origensImpedimento.push(magistrado.origemImpedimento);
            }
        }

        return mapaMagistrados.values().toArray();
    }

    get mapaMagistradosImpedidosSuspeitos() {
        const mapaImpedimentos = new Map();

        for (const magistradoImpedidoSuspeito of this.magistradosImpedidosSuspeitos) {
            mapaImpedimentos.set(magistradoImpedidoSuspeito.sigla, magistradoImpedidoSuspeito);
        }

        return mapaImpedimentos;
    }

    tooltipMagistrado(magistrado) {
        if (!magistrado.origensImpedimento) {
            return '';
        }

        return (
            <Box>
                <ul style={{ listStyleType: 'circle' }}>
                    {magistrado.origensImpedimento.map((origem) => {
                        return (
                            <li key={this.nextKey}>
                                <Typography fontSize={'0.9rem'}>{origem}</Typography>
                            </li>
                        );
                    })}
                </ul>
            </Box>
        );
    }

    renderMagistrados(listaMagistrado) {
        const processo = this.processo;
        let idMagistrado = 0;

        return listaMagistrado.map((magistrado) => {
            if (!magistrado) {
                return null;
            }

            const nome = magistrado.nome || '';
            const sigla = magistrado.sigla || '';
            let papel = '';

            if (processo.relator && processo.relator.nome === nome && processo.relator.sigla === sigla) {
                papel += ` (Relator${processo?.relator?.genero?.startsWith('F') ? 'a)' : ')'}`;
            }

            if (processo.redator && processo.redator.nome === nome && processo.redator.sigla === sigla) {
                papel += ` (Redator${processo?.redator?.genero?.startsWith('F') ? 'a)' : ')'}`;
            }

            if (
                this.presidenteSessao &&
                nome === this.presidenteSessao.magistrado.nome &&
                this.presidenteSessao.magistrado.sigla === sigla
            ) {
                papel += ' (Presidente)';
            }

            return (
                <Stack
                    direction={'row'}
                    key={`magistrado${++idMagistrado}`}
                    sx={{ alignItems: 'center' }}
                >
                    <Tooltip
                        placement="top"
                        title={this.tooltipMagistrado(magistrado)}
                    >
                        <li
                            style={{
                                listStyleType: 'circle',
                                textAlign: 'left',
                                marginLeft: '16px',
                                marginBottom: '4px',
                                color: magistrado.cor || 'black'
                            }}
                            data-testid={`magistrado-${magistrado.sigla}`}
                        >
                            <Typography
                                display="inline"
                                fontWeight={magistrado.fontWeight || 'normal'}
                            >{`[${magistrado.sigla}] ${nome}${papel}`}</Typography>
                        </li>
                    </Tooltip>
                    {this.renderSubstituicao(magistrado)}
                </Stack>
            );
        });
    }

    renderSubstituicao(magistrado) {
        return (
            <Box sx={{ ml: '4px' }}>
                {magistrado.magistradoSubstituto && magistrado.magistradoSubstituto.sigla.length > 0 ? (
                    <Tooltip
                        placement="top"
                        title={<Typography>{this.tooltipSubstituicao(magistrado)}</Typography>}
                    >
                        <IconeSubstituicao style={{ fillOpacity: 0.8, color: red[900] }} />
                    </Tooltip>
                ) : null}
                {magistrado.magistradoSubstituido ? (
                    <Tooltip
                        placement="top"
                        title={<Typography>{this.tooltipSubstituicao(magistrado)}</Typography>}
                    >
                        <IconeSubstituicao style={{ fillOpacity: 0.8, color: green[900] }} />
                    </Tooltip>
                ) : null}
            </Box>
        );
    }

    getNomeMagistrado(sigla) {
        const magistrado = this.dominioStore.magistradosComSigla.find((magist) => magist.sigla === sigla);
        return magistrado ? magistrado.nome : '';
    }

    get linkConsultaProcesso() {
        return this.registro?.detalhes?.links?.find((link) => link.id === 'pje' || link.id === 'esij');
    }

    existePresidenteComposicao() {
        return this.registro && this.composicao.some((composicao) => composicao.magistrado.presidenteSessao === true);
    }

    isImpedido(sigla) {
        const impedimentos = this.registro?.impedimentos ?? [];
        return impedimentos.some((imp) => imp.magistrado?.sigla === sigla);
    }

    isObedeceQuorumComposicao() {
        return QuorumBusiness.isObedeceQuorumComposicao(this.registro, this.sessaoStore.sessaoSelecionada?.quorum);
    }

    alertasComposicaoTooltipTitle(alertas) {
        const tamanhoFonte = '0.9rem';

        return (
            <Box>
                <Typography fontSize={tamanhoFonte}>{'ALERTA' + (alertas.length === 1 ? ':' : 'S:')}</Typography>
                <ul style={{ listStyleType: 'circle' }}>
                    {alertas.map((alerta) => {
                        return (
                            <li key={this.nextKey}>
                                <Typography fontSize={tamanhoFonte}>{alerta}</Typography>
                            </li>
                        );
                    })}
                </ul>
            </Box>
        );
    }

    renderAlertasComposicao() {
        const alertas = [];

        if (this.registro.composicao && this.registro.composicao.length > 0) {
            if (!this.existePresidenteComposicao()) {
                alertas.push('Não há presidente na composição.');
            }

            if (!this.isObedeceQuorumComposicao(this.registro, this.sessaoStore.sessaoSelecionada?.quorum)) {
                alertas.push('Quórum não atingido na composição.');
            }

            if (this.isComponenteHabilitado('ConsultaComposicaoPadrao') && this.isComposicaoPadraoDiferente) {
                alertas.push('A composição definida para o processo difere da composição padrão do OJC.');
            }
        }

        if (alertas.length === 0) {
            return null;
        }

        return (
            <Tooltip title={this.alertasComposicaoTooltipTitle(alertas)}>
                <Warning style={{ fill: 'darkorange', marginRight: '5px' }} />
            </Tooltip>
        );
    }

    get isComposicaoPadraoDiferente() {
        return !this.sessaoStore.isComposicaoIgualPadraoOJC(this.registro.composicao);
    }

    get composicaoHabilitada() {
        return this.registro.sessaoId;
    }

    ocultarRepresentantes() {
        return this.isPreSessao;
    }

    render() {
        return this.observableProps.visualizacaoPregaoProcesso
            ? this.renderInformacoesGeraisProcessoVisualizacaoPregaoProcesso()
            : this.renderInformacoesGeraisProcesso();
    }

    renderInformacoesGeraisProcesso() {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: '1' }}>
                <div style={{ display: 'flex', flexGrow: '4', paddingBottom: '8px' }}>
                    <div style={{ display: 'flex', flexGrow: '1', width: '50%', overflowY: 'auto' }}>
                        {this.registro.detalhes && Object.keys(this.registro.detalhes).length > 0 ? (
                            this.renderDetalhes()
                        ) : (
                            <div style={{ display: 'flex' }}>
                                <CircularProgress
                                    disableShrink={true}
                                    style={{ margin: '10px' }}
                                />
                                <Typography
                                    style={{ margin: '5px', paddingTop: '10px' }}
                                    variant="subtitle1"
                                >
                                    Carregando informações do processo
                                </Typography>
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            boxShadow: 'rgba(0, 0, 0, 0.4) 2px 0px 0px 0px',
                            width: '1px',
                            margin: '0px 4px',
                            height: '100%',
                            color: 'white'
                        }}
                    />
                    {this.renderComposicao()}
                </div>
                <Divider style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', marginBottom: '5px' }} />
                <PartesProcesso
                    expandido={this.observableProps.expandido}
                    registro={this.registro}
                />
                <PedidosSustentacaoOral registro={this.registro} />
            </div>
        );
    }

    renderInformacoesGeraisProcessoVisualizacaoPregaoProcesso() {
        return (
            <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        flex: 1,
                        overflow: 'auto'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            gap: 2,
                            p: 2,
                            border: '1px solid rgba(0, 0, 0, 0.12)'
                        }}
                    >
                        <Box>
                            {this.registro.detalhes && Object.keys(this.registro.detalhes).length > 0 ? (
                                this.renderDetalhesPregaoProcesso()
                            ) : (
                                <Box style={{ display: 'flex' }}>
                                    <CircularProgress
                                        disableShrink={true}
                                        style={{ margin: '10px' }}
                                    />
                                    <Typography
                                        style={{ margin: '5px', paddingTop: '10px' }}
                                        variant="subtitle1"
                                    >
                                        Carregando informações do processo
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {this.renderComposicao()}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            gap: 2,
                            p: 2,
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderLeft: 'none'
                        }}
                    >
                        <PartesProcesso
                            ocultarRepresentantes
                            visualizacaoVertical
                            registro={this.registro}
                            visualizacaoVerticalEstilos={{ maxHeight: 'auto' }}
                        />
                        <Divider />
                        <PedidosSustentacaoOral
                            semDivisor
                            visualizacaoVertical
                            registro={this.registro}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', width: '100%' }}>
                    <ComplementoAnotacao
                        desabilitado
                        larguraTotal
                        onOpenAnotacoesInformacoesProcesso={this.observableProps.onOpenAnotacoesInformacoesProcesso}
                        registro={this.registro}
                    />
                </Box>
            </Box>
        );
    }

    mensagensAlertaDetalhes() {
        if (!this.registro.alertasImpedimento || this.registro.alertasImpedimento.length === 0) {
            return <Typography display="inline">Não há alertas</Typography>;
        }

        // Achata a lista de alertas e motivos em uma única lista
        const todosMotivos = this.registro.alertasImpedimento.flatMap((alerta) =>
            alerta.motivos.map((motivo) => ({
                alertaId: alerta.id || alerta.tipo || 'alerta',
                motivo
            }))
        );

        return todosMotivos.map(({ alertaId, motivo }, index) => {
            const ehUltimo = index === todosMotivos.length - 1;

            return (
                <Typography key={`${alertaId}-${motivo}`}>
                    {motivo}
                    {ehUltimo ? '' : '; '}
                </Typography>
            );
        });
    }

    renderDetalhes() {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingRight: '5px',
                    minHeight: '90px',
                    maxHeight: this.observableProps.expandido ? 'none' : '40px'
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Stack
                        direction={'row'}
                        sx={{ alignItems: 'center', mb: '2px' }}
                    >
                        <Typography
                            color={COR_TITULO_INFORMACOES_PROCESSO}
                            display="inline"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Número:&nbsp;
                        </Typography>
                        {this.linkConsultaProcesso ? (
                            <Tooltip title={<Typography>{this.linkConsultaProcesso.descricao}</Typography>}>
                                <Link
                                    href={this.linkConsultaProcesso.uri.replaceAll('&amp;', '&')}
                                    target="_blank"
                                    underline="hover"
                                >
                                    <Typography display="inline">{this.processo.numeroCompleto}</Typography>
                                </Link>
                            </Tooltip>
                        ) : (
                            <Typography display="inline">{this.processo.numeroCompleto}</Typography>
                        )}
                        <IconeConsultaPublica registro={this.registro} />
                    </Stack>
                    <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                        <Typography
                            display="inline"
                            sx={{ fontWeight: 'bold', color: COR_TITULO_INFORMACOES_PROCESSO }}
                        >
                            Classe:&nbsp;
                        </Typography>
                        <DescricaoClasseProcesso registro={this.registro} />
                    </Box>
                    {this.renderUltimaDitribuicaoEm()}
                    {this.renderUltimaConclusaoAutosEm()}
                    {this.renderUltimaLiberacaoPautaEm()}
                    {this.renderLiberacaoPauta()}
                    {this.renderUltimoAndamento()}
                    <JulgamentosAnteriores registro={this.registro} />
                </div>
                {this.renderAssuntosProcessuais()}
                <Indicadores registro={this.registro} />
                {this.renderAlertasImpedimento(this.mensagensAlertaDetalhes)}
            </div>
        );
    }

    renderDetalhesPregaoProcesso() {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingRight: '5px',
                    maxHeight: 'auto'
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: '2px' }}>
                        <Typography
                            color={COR_TITULO_INFORMACOES_PROCESSO}
                            display="inline"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Número:&nbsp;
                        </Typography>
                        <LabelNumeroProcesso
                            exibirIcone={false}
                            registro={this.registro}
                        />
                        <IconeConsultaPublica registro={this.registro} />
                    </Box>

                    <Stack
                        direction={'row'}
                        sx={{ alignItems: 'center', mb: '2px' }}
                    >
                        <Typography
                            color={COR_TITULO_INFORMACOES_PROCESSO}
                            display="inline"
                            sx={{ fontWeight: 'bold' }}
                        >
                            Relator{this.processo?.relator?.genero?.startsWith('F') ? 'a' : ''}:&nbsp;
                        </Typography>
                        <Typography display="inline">
                            [{this.registro?.processo?.relator?.sigla}] {this.registro?.processo?.relator?.nome}
                        </Typography>
                    </Stack>
                    <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                        <Typography
                            display="inline"
                            sx={{ fontWeight: 'bold', color: COR_TITULO_INFORMACOES_PROCESSO }}
                        >
                            Classe:&nbsp;
                        </Typography>
                        <DescricaoClasseProcesso registro={this.registro} />
                    </Box>
                    {Array.isArray(this.registro?.informacoesAdicionais?.RetornoVistaRegimental) &&
                        this.registro.informacoesAdicionais.RetornoVistaRegimental.length > 0 && (
                            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                                {' '}
                                <Typography
                                    display="inline"
                                    sx={{ fontWeight: 'bold', color: COR_TITULO_INFORMACOES_PROCESSO }}
                                >
                                    {this.tituloVistor()}
                                    :&nbsp;
                                </Typography>
                                <Typography
                                    display="inline"
                                    variant="body1"
                                >
                                    {this.registro.informacoesAdicionais.RetornoVistaRegimental.map(
                                        (vista) => vista.magistrado?.nome
                                    ).join(', ')}
                                </Typography>
                            </Box>
                        )}
                    <JulgamentosAnteriores registro={this.registro} />
                </div>
                {this.renderAlertasImpedimento()}
            </div>
        );
    }

    tituloVistor() {
        const vistores = this.registro.informacoesAdicionais?.RetornoVistaRegimental ?? [];

        if (vistores.length > 1) {
            const todasFemininas = vistores.every((vista) => vista.magistrado?.genero === 'F');
            return todasFemininas ? 'Vistoras' : 'Vistores';
        }

        const isFeminino = vistores[0]?.magistrado?.genero === 'F';
        return isFeminino ? 'Vistora' : 'Vistor';
    }

    renderAlertasImpedimento() {
        if (this.registro.processo.origem !== Configuracao.ORIGEM_PJE) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    display="inline"
                    sx={{ fontWeight: 'bold', color: COR_TITULO_INFORMACOES_PROCESSO }}
                >
                    Alertas de impedimento:&nbsp;
                </Typography>
                {this.mensagensAlertaDetalhes()}
            </Box>
        );
    }

    renderAssuntosProcessuais() {
        const assuntos = this.registro.detalhes?.assuntos ?? [];

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ fontWeight: 'bold' }}
                >
                    Assuntos Processuais:&nbsp;
                </Typography>
                {assuntos.length > 0 ? (
                    assuntos.map((assunto, index) => {
                        const isUltimo = index === assuntos.length - 1;

                        return (
                            <Typography
                                display="inline"
                                key={assunto.codigo || assunto.descricao}
                            >
                                {assunto.descricao}
                                {isUltimo ? '' : ' / '}
                            </Typography>
                        );
                    })
                ) : (
                    <Typography display="inline">Não há assuntos processuais</Typography>
                )}
            </Box>
        );
    }

    renderUltimoAndamento() {
        if (!this.registro.detalhes.ultimoAndamento) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ fontWeight: 'bold' }}
                >
                    Último andamento:&nbsp;
                </Typography>
                <Typography display="inline">
                    {this.registro.detalhes.ultimoAndamento.id +
                        ' - ' +
                        this.registro.detalhes.ultimoAndamento.descricao}
                </Typography>
            </Box>
        );
    }

    renderUltimaConclusaoAutosEm() {
        if (!this.registro.detalhes.ultimaConclusaoAutosEm) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ fontWeight: 'bold' }}
                >
                    Conclusão para julgamento:&nbsp;
                </Typography>
                <Typography display="inline">
                    {moment(this.registro.detalhes.ultimaConclusaoAutosEm, 'DD-MM-YYYY-HH-mm-ss').format(
                        'DD/MM/YYYY HH:mm:ss'
                    )}
                </Typography>
            </Box>
        );
    }

    renderUltimaDitribuicaoEm() {
        if (!this.registro.detalhes.ultimaDitribuicaoEm) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ fontWeight: 'bold' }}
                >
                    Distribuição do processo:&nbsp;
                </Typography>
                <Typography display="inline">
                    {moment(this.registro.detalhes.ultimaDitribuicaoEm, 'DD-MM-YYYY-HH-mm-ss').format(
                        'DD/MM/YYYY HH:mm:ss'
                    )}
                </Typography>
            </Box>
        );
    }

    renderUltimaLiberacaoPautaEm() {
        if (!this.registro.detalhes.ultimaLiberacaoPautaEm) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ fontWeight: 'bold' }}
                >
                    Liberação para inclusão em pauta:&nbsp;
                </Typography>
                <Typography display="inline">
                    {moment(this.registro.detalhes.ultimaLiberacaoPautaEm, 'DD-MM-YYYY-HH-mm-ss').format(
                        'DD/MM/YYYY HH:mm:ss'
                    )}
                </Typography>
            </Box>
        );
    }

    renderLiberacaoPauta() {
        if (!this.registro.liberacaoNaPauta) {
            return null;
        }

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={COR_TITULO_INFORMACOES_PROCESSO}
                    display="inline"
                    sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES, fontWeight: 'bold' }}
                >
                    Liberação do voto pelo magistrado:&nbsp;
                </Typography>
                <Typography display="inline">
                    {moment(this.registro.liberacaoNaPauta, 'DD-MM-YYYY-HH-mm-ss').format('DD/MM/YYYY HH:mm:ss')}
                </Typography>
            </Box>
        );
    }

    renderComposicao() {
        return (
            <div
                style={{
                    display: 'flex',
                    flexGrow: this.observableProps.visualizacaoPregaoProcesso ? 'auto' : '1',
                    width: this.observableProps.visualizacaoPregaoProcesso ? 'auto' : '50%',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        paddingLeft: '5px',
                        minHeight: '90px',
                        maxHeight:
                            this.observableProps.visualizacaoPregaoProcesso || this.observableProps.expandido
                                ? 'auto'
                                : '40px'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        {this.renderAlertasComposicao()}
                        <Typography
                            color={COR_TITULO_INFORMACOES_PROCESSO}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Composição atual:
                        </Typography>
                    </div>
                    {this.renderMagistrados(this.magistradosPresentes)}
                    {this.magistradosImpedidosSuspeitos.length > 0 && (
                        <Box sx={{ m: '5px' }}>
                            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                                <Typography
                                    color={COR_TITULO_INFORMACOES_PROCESSO}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Impedidos/Suspeitos:
                                </Typography>
                            </Box>
                            {this.renderMagistrados(this.magistradosImpedidosSuspeitos)}
                        </Box>
                    )}
                    {this.magistradosAusentes.length > 0 && (
                        <Box sx={{ m: '5px' }}>
                            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                                <Typography
                                    color={COR_TITULO_INFORMACOES_PROCESSO}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Ausentes:
                                </Typography>
                            </Box>
                            {this.renderMagistrados(this.magistradosAusentes)}
                        </Box>
                    )}
                </div>
            </div>
        );
    }
}

export default inject('stores')(observer(InformacoesProcesso));
